import { useEventListener } from "hooks/useEventListener";
import { IndexedInventory } from "types/inventory";
import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { dispatchBulkActionComplete } from "components/DataGrid/components/RevivnToolbar";
import { useAlert } from "hooks/useAlert";
import { useConfirmation } from "hooks/useConfirmation";
import { api } from "services/api.service";
import { bulkEditError } from "utils/bulkEditError";

export const BulkPrintAction = () => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedRows, setSelectedRows] = useState<IndexedInventory[]>([]);
  const { alertError, alertSuccess } = useAlert();
  const { confirm } = useConfirmation();

  useEventListener("bulkPrint", (event) => {
    if (event instanceof CustomEvent) {
      const details = event.detail as { selectedRows: IndexedInventory[] };
      setOpen(true);
      setSelectedRows(details.selectedRows);
    }
  });

  const markPrinted = (rows: IndexedInventory[], skips: string[] = []) => {
    const printedAt = new Date().toUTCString();
    const flags = Object.fromEntries(skips.map((skip) => [skip, true]));
    setSaving(true);
    return api
      .put("/inventories/bulk/edit", {
        inventories: rows.map((inv) => ({ id: inv.id, printedAt, label: `${inv.pickupId} - ${inv.lineId}`, ...flags })),
      })
      .then(() => {
        alertSuccess(`Successfully marked inventory as printed`);
        dispatchBulkActionComplete();
        setOpen(false);
      })
      .catch((error) => {
        const skip = bulkEditError(error);
        if (skip.status === 422 && skip.param && !skips.includes(skip.param)) {
          const param = skip.param;
          // The labels are already printed, so ask the platform's question rather than failing.
          // One answer covers every selected row, because the platform only names the first it finds.
          setOpen(false);
          confirm({
            message: `${skip.message ?? "Mark these items as printed anyway?"} Confirming applies to all ${rows.length} selected items.`,
            context: param,
            handler: () => markPrinted(rows, [...skips, param]),
          });
          return;
        }
        alertError(`Failed to mark inventory as printed: ${skip.message ?? "please try again"}`);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"Mark inventory as printed?"}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          By clicking yes, we will mark these inventory labels as printed.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>No</Button>
        <Button onClick={() => markPrinted(selectedRows)} disabled={saving}>
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
